import React, { useState } from "react";
import axios from "axios";
import { useHotkeys } from "react-hotkeys-hook";
import { Badge, Button, Dataflow03, Drawer, SearchLg, TextInput } from "@opendate-app/setlist";

import { toastError } from "../../../shared/toastHelper";
import VenueSelectionModal from "../shared/VenueSelectionModal";
import AiAutomationPrompt from "./AiAutomationPrompt";
import CountBadge from "../../shared/components/CountBadge";

const SEARCH_INPUT_ID = "automation-template-search";

const sectionStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

const dividerStyle = {
  height: 1,
  background: "var(--sl-border-secondary)",
};

const headingStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  margin: 0,
  fontSize: 16,
  fontWeight: 600,
  lineHeight: "24px",
  color: "var(--sl-text-primary)",
};

const templateCardStyle = {
  background: "var(--sl-bg-primary)",
  border: "1px solid var(--sl-border-secondary)",
  borderRadius: 12,
};

const templateCardHeaderStyle = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "12px 20px 12px 12px",
  borderBottom: "1px solid var(--sl-border-secondary)",
};

const templateIconStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  width: 32,
  height: 32,
  border: "1px solid var(--sl-border-secondary)",
  borderRadius: 6,
  color: "var(--sl-fg-secondary)",
};

const templateNameStyle = {
  flex: "1 0 0",
  margin: 0,
  fontSize: 16,
  fontWeight: 600,
  lineHeight: "24px",
  color: "var(--sl-text-secondary)",
};

const templateDescriptionStyle = {
  margin: 0,
  padding: 16,
  fontSize: 14,
  lineHeight: "20px",
  color: "var(--sl-text-tertiary)",
};

const templateActionsStyle = {
  padding: "0 12px 12px",
};

const CreateCampaignPane = ({
  isOpen,
  onClose,
  customCampaignTemplates,
  aiAutomationBuilder,
  customCampaigns,
  team,
  teamMembership,
  csrfToken,
  enforceMarketingVenueAccessControl,
  venueOwnerships
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showVenueModal, setShowVenueModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingCancel, setPendingCancel] = useState(null);

  useHotkeys("meta+slash", (e) => {
    e.preventDefault();
    if (isOpen) {
      document.getElementById(SEARCH_INPUT_ID)?.focus();
    }
  }, { enableOnFormTags: true });

  const getTemplateUsageCount = (templateId) => {
    return customCampaigns.filter(campaign =>
      campaign.copied_from_custom_campaign_id === templateId
    ).length;
  };

  const fetchTeamMembershipAndExecute = async (action, onCancel = () => {}) => {
    if (!enforceMarketingVenueAccessControl) {
      action(null);
      return;
    }

    try {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      const { data } = await axios.get(`/teams/${team.id}/team_memberships/${teamMembership.id}`);

      if (data.selected_marketing_venue_ownership?.id) {
        action(data.selected_marketing_venue_ownership.id);
      } else {
        setPendingAction(() => action);
        setPendingCancel(() => onCancel);
        setShowVenueModal(true);
      }
    } catch (error) {
      onCancel();
      toastError("Could not fetch venue information, please try again");
    }
  };

  const handleBuildFromScratch = () => {
    fetchTeamMembershipAndExecute((venueOwnershipId) => {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      axios.post("/teams/" + team.id + "/custom_campaigns/", {
        custom_campaign: {
          state: "created",
          venue_ownership_id: venueOwnershipId,
        }
      })
        .then(({ data }) => {
          window.location.href = `/teams/${team.id}/custom_campaigns/${data.id}`;
        })
        .catch(_error => toastError("Could not create automation, please try again"));
    });
  };

  const handleUseTemplate = (template) => {
    fetchTeamMembershipAndExecute((venueOwnershipId) => {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      axios.post(`/teams/${team.id}/custom_campaigns/create_from_template`, {
        id: template.id,
        venue_ownership_id: venueOwnershipId,
      })
        .then(({ data }) => {
          window.location.href = `/teams/${team.id}/custom_campaigns/${data.id}`;
        })
        .catch(_error => toastError("Could not copy automation, please try again"));
    });
  };

  const handleVenueModalConfirm = (venueOwnershipId) => {
    if (pendingAction) {
      pendingAction(venueOwnershipId);
      setShowVenueModal(false);
      setPendingAction(null);
      setPendingCancel(null);
    }
  };

  const handleVenueModalClose = () => {
    if (pendingCancel) pendingCancel();
    setShowVenueModal(false);
    setPendingAction(null);
    setPendingCancel(null);
  };

  const filteredTemplates = customCampaignTemplates.filter(template =>
    template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (template.description && template.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <>
      <Drawer
        open={isOpen}
        onClose={onClose}
        width={480}
        closeOnEsc={!showVenueModal}
        title="Create an Automation"
        subtitle="Build from scratch or select a template to get started"
      >
        <div style={sectionStyle}>
          {aiAutomationBuilder?.enabled && (
            <AiAutomationPrompt
              csrfToken={csrfToken}
              team={team}
              examples={aiAutomationBuilder.examples}
              placeholder={aiAutomationBuilder.placeholder}
              resolveVenueOwnershipId={fetchTeamMembershipAndExecute}
            />
          )}
          <Button variant="secondary-color" icon={Dataflow03} onClick={handleBuildFromScratch} style={{ width: "100%" }}>
            Build from Scratch
          </Button>
          <div style={dividerStyle} />
          <h3 style={headingStyle}>
            Templates
            <CountBadge count={customCampaignTemplates.length} />
          </h3>
          <TextInput
            id={SEARCH_INPUT_ID}
            leadingIcon={SearchLg}
            trailingText="⌘/"
            placeholder="Search templates"
            aria-label="Search templates"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {filteredTemplates.map((template) => {
            const usageCount = getTemplateUsageCount(template.id);
            return (
              <div key={template.id} style={templateCardStyle}>
                <div style={templateCardHeaderStyle}>
                  <span style={templateIconStyle}>
                    {template.fa_icon ? (
                      <i className={template.fa_icon} style={{ fontSize: 16 }} aria-hidden="true" />
                    ) : (
                      <Dataflow03 width={16} height={16} aria-hidden="true" />
                    )}
                  </span>
                  <p style={templateNameStyle}>{template.name}</p>
                  {usageCount > 0 && (
                    <Badge color="gray" size="sm">Used {usageCount} {usageCount === 1 ? "Time" : "Times"}</Badge>
                  )}
                </div>
                {template.description && (
                  <p style={templateDescriptionStyle}>{template.description}</p>
                )}
                <div style={templateActionsStyle}>
                  <Button
                    size="sm"
                    variant={usageCount > 0 ? "secondary" : "secondary-color"}
                    onClick={() => handleUseTemplate(template)}
                  >
                    Use This Template
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </Drawer>

      <VenueSelectionModal
        isOpen={showVenueModal}
        onClose={handleVenueModalClose}
        onConfirm={handleVenueModalConfirm}
        venueOwnerships={venueOwnerships}
        title="Select a Venue"
        subtitle="Choose a venue to associate with this automation"
      />
    </>
  );
};

export default CreateCampaignPane;
